import React, { useEffect, useRef, useState } from "react"
import FileDownload from "js-file-download"
import {
	Grid,
	Avatar,
	Typography,
	IconButton,
	CircularProgress, Popover,
	List,
	ListItem,
	ListItemText
} from "@material-ui/core"

import { Alert } from "@material-ui/lab"

import {
	ArrowBackIosOutlined as CloseChatIcon,
	MoreVert as MoreOptionsIcon,
	Link as LinkIcon,
	Sync as SyncIcon,
	GetApp as GetAppIcon,
	LockOutlined as BlockContactIcon,
	LockOpenOutlined as UnblockContactIcon
} from "@material-ui/icons"

import {
	Divider, InfoDialog, Notification, TextCopy
} from "@/components"

import { AttendanceStatus, IAttendance } from "@/protocols/channel"

import { useChatGlobalStateStore } from "@/store/ChatGlobalState"
import useSocket from "@/hooks/useSocket"
import useCustomMemo from "@/hooks/useCustomMemo"
import useSnooze from "@/hooks/useSnooze"
import useContactDisplay from "@/hooks/useContactDisplay"

import ManageAttendance from "@/pages/Attendance/Chat/ConversationPanel/ConversationHeader/ManageAttendance"
import useStyles from "@/pages/Attendance/Chat/ConversationPanel/ConversationHeader/styles"
import AssignAttendance from "@/pages/Attendance/Chat/ConversationPanel/ConversationHeader/AssignAttendance"
import VoiceCallButton from "@/components/VoiceCall/VoiceCallButton"
import NewSnooze from "@/pages/Attendance/Chat/ConversationPanel/ConversationHeader/NewSnooze"
import ChatGroupPermissionGate from "@/components/PermissionGate/ChatGroupPermissionGate"
import ContactGroupPermissionGate from "@/components/PermissionGate/ContactGroupPermissionGate"

import { isSmallScreen } from "@/utils/checkDevice"
import { keepOnlyDigits } from "@/utils/phone"
import { getPhoneNumber, isLidContact } from "@/utils/contact"
import { MIN_ISO_DATE_STRING } from "@/utils/time"
import { buildChatLink } from "@/utils/chat"

import ApiService from "@/services/Api"
import HardCoded from "@/services/HardCoded"

import useCustomStyles from "@/styles/custom"
import ChannelRestrictionGate from "@/components/ChannelRestrictionGate"

import BlockContactDialog from "@/pages/Admin/ClientCatalog/BlockContactDialog"
import { useGlobalStateStore } from "@/store/GlobalState"
import UnblockContactDialog from "@/pages/Admin/ClientCatalog/UnblockContactDialog"
import LidContactWarning, { LID_CONTACT_WARNING_TEXT } from "@/components/LidContactWarning"

type ConversationHeaderProps = {
	onClose: () => void
	onClick: () => void
}

const ConversationHeader: React.FC<ConversationHeaderProps> = (props) => {
	const { onClick, onClose } = props

	const [isSyncChatMessages, setIsSyncChatMessages] = useState<boolean>(false)
	const chatGlobalStateStore = useChatGlobalStateStore()
	const globalStateStore = useGlobalStateStore()
	const socket = useSocket()
	const customClasses = useCustomStyles()
	const classes = useStyles()
	const { getDisplayName, getDisplayPhoneNumber } = useContactDisplay()
	const client = chatGlobalStateStore.client.current
	const inboxChannelChat = chatGlobalStateStore.chat.current
	const contactPhoneNumber = getPhoneNumber(client)

	const isLid = isLidContact(client)
	const inboxChannelChatId = inboxChannelChat?.id as number
	const currentChatAttendanceStatus = inboxChannelChat?.attendance?.status as AttendanceStatus
	const isAbleToAssignAttendance = (["active", "waiting"] as AttendanceStatus[]).includes(currentChatAttendanceStatus)

	const isCurrentUserAttendanceOwner = inboxChannelChat?.attendance?.userId === globalStateStore.user.id
	const isAttendanceStatusTakeOrStart = currentChatAttendanceStatus === "finished" || currentChatAttendanceStatus === "waiting"
	const canManageAttendanceWithoutPermission = isCurrentUserAttendanceOwner || isAttendanceStatusTakeOrStart
	const canTransferWithoutPermission = isCurrentUserAttendanceOwner

	const [moreOptionsMenuAnchorEl, setMoreOptionsMenuAnchorEl] = React.useState<null | HTMLElement>(null)
	const moreOptionsMenuIsOpen = Boolean(moreOptionsMenuAnchorEl)

	const isWabaChannel = globalStateStore.instance.current_channel_type === "waba"
	const inboxChannelId = globalStateStore.currentChannel?.id as number
	const instanceId = globalStateStore.instance?.instance_id

	const [isBlockedContact, setIsBlockedContact] = useState<boolean>(false)

	useEffect(() => {
		const wabaContact = client?.contacts?.find(contact => contact.channel_type === "waba")
		setIsBlockedContact(Boolean(wabaContact?.data?.is_blocked))
	}, [client?.id])

	const blockContactOnSuccess = () => {
		setIsBlockedContact(true)
	}

	const unblockContactOnSuccess = () => {
		setIsBlockedContact(false)
	}

	const {
		handleCancelSnooze
	} = useSnooze({ inboxChannelChatId })

	const handleMoreOptionsMenuClose = () => {
		setMoreOptionsMenuAnchorEl(null)
	}

	const handleMoreOptionsMenuClick = (event: React.MouseEvent<HTMLElement>) => {
		setMoreOptionsMenuAnchorEl(event.currentTarget)
	}

	const getSyncStartDateInMilliseconds = () => {
		const syncStartDateAsDate = new Date(MIN_ISO_DATE_STRING)

		const syncStartDateAsMilliseconds = syncStartDateAsDate.getTime()

		return syncStartDateAsMilliseconds
	}

	const handleSyncCurrentChatMessages = async () => {
		setIsSyncChatMessages(true)

		handleMoreOptionsMenuClose()

		try {
			if (inboxChannelChat) {
				const { channelType, id, inboxChannelId } = inboxChannelChat

				const syncStartDateAsMilliseconds = getSyncStartDateInMilliseconds()

				const socketResponse = await socket.scheduleChatMessagesSync(
					{
						channelType,
						inboxChannelChatId: id,
						inboxChannelId,
						afterDate: syncStartDateAsMilliseconds
					}
				)

				if (socketResponse) {
					Notification.success({ message: "As mensagens dessa conversa foram sincronizadas." })
				} else {
					Notification.error({ message: "Não foi possível sincronizar." })
				}
			} else {
				Notification.info({ message: "Conversa não encontrada." })
			}
		} catch (error) {
			Notification.error({ message: "Houve um erro." })
		}

		setIsSyncChatMessages(false)
	}

	const handleShareChatLink = async () => {
		handleMoreOptionsMenuClose()

		const shareChatUrl = `${window.location.origin}${buildChatLink(client?.id)}`

		InfoDialog.open({
			title: "COMPARTILHAR LINK",
			openDialog: true,
			children: (
				<Grid
					container
					alignContent="center"
				>
					<Grid item xs={12}>
						<Alert severity="warning" icon={false}>
							Para que o atendente consiga abrir o link e visualizar a conversa, ele precisa estar autenticado no sistema da Letalk, pois é um <b>link privado</b>.
						</Alert>
					</Grid>

					<Divider size={2} orientation="horizontal" />

					<TextCopy
						text={shareChatUrl}
						onSuccessMessage="Link copiado com sucesso!"
					/>
				</Grid>
			)
		})
	}

	const handleExportMessages = async () => {
		try {
			const response = await ApiService.get(
				"/inbox/channel/chat/messages/export-zip",
				{
					params: {
						inboxChannelChatId: inboxChannelChatId
					},
					responseType: "blob"
				}
			)

			FileDownload(response.data, `chat_${inboxChannelChatId}_messages.zip`)
		} catch (error) {
			Notification.error({ message: "Houve um erro." })
		}
	}

	const showTitleName = (keepOnlyDigits(client?.nickname || "") === contactPhoneNumber) && (inboxChannelChat?.type !== "group")
	const canBlockContact = isWabaChannel

	const listenersRegistered = useRef(false)

	if (socket && !listenersRegistered.current) {
		listenersRegistered.current = true

		const updateClientBlockedStatus = async (isBlocked: boolean) => {
			const currentClient = chatGlobalStateStore.client.current
			if (!currentClient) return

			const updatedContacts = currentClient.contacts?.map(contact => {
				const isWabaChannel = contact.channel_type === "waba"

				if (!isWabaChannel) {
					return contact
				}

				const updatedData = {
					...contact.data,
					is_blocked: isBlocked
				}

				const updatedContact = {
					...contact,
					data: updatedData
				}

				return updatedContact
			})

			await chatGlobalStateStore.client.updateById(currentClient.id, {
				contacts: updatedContacts
			})
		}

		socket.onBlockContact(async (attendance: IAttendance) => {
			if (attendance.inboxChannelChatId === inboxChannelChatId) {
				await updateClientBlockedStatus(true)
				setIsBlockedContact(true)
			}
		})

		socket.onUnblockContact(async (attendance: IAttendance) => {
			if (attendance.inboxChannelChatId === inboxChannelChatId) {
				setIsBlockedContact(false)
			}
		})
	}

	return useCustomMemo(() => (
		<>
			{inboxChannelChat && (
				<>
					{isSmallScreen && (
						<>
							<IconButton
								onClick={() => onClose()}
							>
								<CloseChatIcon />
							</IconButton>

							<Divider orientation="vertical" size={2} />
						</>
					)}

					<Grid item xs>
						<Grid
							container
							spacing={2}
							alignItems="center"
							style={{ cursor: "pointer" }}
							onClick={() => onClick()}
							wrap="nowrap"
						>

							<Grid item>
								<Avatar src={client?.picture_url} />
							</Grid>

							<Grid item xs>
								<Grid
									container
									direction="column"
									alignItems="flex-start"
								>
									<Grid item>
										<Grid
											container
											alignItems="center"
											wrap="nowrap"
										>
											<Grid item>
												<Typography
													variant="h2"
													color="textPrimary"
													className={customClasses.inlineText}
												>
													{getDisplayName(client?.nickname, contactPhoneNumber)}

													{
														showTitleName && (
															<Typography
																variant="caption"
																color="textPrimary"
																className={classes.inlineTextName}
															>
																~{getDisplayName(client?.name, contactPhoneNumber)}
															</Typography>
														)
													}
												</Typography>

											</Grid>

											<Grid item style={{ marginLeft: 4 }}>
												{
													isLid && (
														<LidContactWarning />
													)
												}
											</Grid>
										</Grid>
									</Grid>

									<Grid item>
										<Typography
											variant="caption"
											color="textPrimary"
											className={customClasses.inlineText}
										>
											{getDisplayPhoneNumber(contactPhoneNumber)}
										</Typography>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>

					<Divider orientation="vertical" size={2} />

					{isAttendanceStatusTakeOrStart ? (
						<ChatGroupPermissionGate action="start-attendance">
							<ManageAttendance attendanceStatus={currentChatAttendanceStatus} handleCancelSnooze={handleCancelSnooze} />
						</ChatGroupPermissionGate>
					) : (
						<ChatGroupPermissionGate
							action="finish-others-attendance"
							partialBlockRule={() => canManageAttendanceWithoutPermission}
						>
							<ManageAttendance attendanceStatus={currentChatAttendanceStatus} handleCancelSnooze={handleCancelSnooze} />
						</ChatGroupPermissionGate>
					)}

					<Divider orientation="vertical" size={2} />

					<Grid>
						<VoiceCallButton
							VoiceCallDialogProps={{
								phoneNumber: contactPhoneNumber,
								name: getDisplayName(client?.nickname, contactPhoneNumber) || client?.name || "",
								pictureUrl: client?.picture_url,
								instanceId
							}}
							disabled={isLid}
							disabledTooltipText={LID_CONTACT_WARNING_TEXT}
						/>

						{isAbleToAssignAttendance && (
							<ChatGroupPermissionGate
								action="transfer-any-chat"
								partialBlockRule={() => canTransferWithoutPermission}
							>
								<AssignAttendance />
							</ChatGroupPermissionGate>
						)}
					</Grid>

					<NewSnooze inboxChannelChatId={inboxChannelChatId} />

					<Grid item>
						<IconButton
							id={"moreOptionsPopover"}
							aria-label="more"
							aria-controls="long-menu"
							aria-haspopup="true"
							onClick={(event) => handleMoreOptionsMenuClick(event)}
						>
							<MoreOptionsIcon />
						</IconButton>

						<Popover
							open={moreOptionsMenuIsOpen}
							anchorEl={moreOptionsMenuAnchorEl}
							onClose={handleMoreOptionsMenuClose}
							anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
							transformOrigin={{ vertical: "bottom", horizontal: "left" }}
						>
							<List>
								<ListItem
									button
									onClick={handleShareChatLink}
									disabled={isSyncChatMessages}
								>
									<LinkIcon />
									<Divider orientation="vertical" size={1} />
									<ListItemText primary="Compartilhar (Link)" />
								</ListItem>

								{
									HardCoded.checkFeatureFlag("canShowExportMessageOption") && (
										<ListItem
											button
											onClick={handleExportMessages}
										>
											<GetAppIcon />
											<Divider orientation="vertical" size={1} />
											<ListItemText primary="Exportar Mensagens" />
										</ListItem>
									)
								}

								<ChannelRestrictionGate
									variant="hover"
									tooltipConfig={{
										tooltipText: "Sincronizar mensagens das últimas 48 horas. Antes de iniciar a sincronização, certifique-se de que a mensagem desejada esteja visível no WhatsApp Web. Após verificar, feche o WhatsApp Web para que o processo de sincronização possa ser realizado corretamente.",
										tooltipDisableRules: false
									}}
								>
									<ListItem
										button
										onClick={handleSyncCurrentChatMessages}
										disabled={isSyncChatMessages}
									>
										{isSyncChatMessages ? (
											<CircularProgress />
										) : (
											<SyncIcon />
										)}
										<Divider orientation="vertical" size={1} />
										<ListItemText primary="Sincronizar Chat" />
									</ListItem>
								</ChannelRestrictionGate>
								{
									canBlockContact && (
										<ContactGroupPermissionGate action="block-unblock-waba">
											{isBlockedContact ? (
												<UnblockContactDialog
													clientId={client?.id as number}
													inboxChannelId={inboxChannelId}
													onSuccess={unblockContactOnSuccess}
												>
													<ListItem
														button>
														<UnblockContactIcon style={{ width: 22, height: 22 }} />
														<Divider orientation="vertical" size={1} />
														<ListItemText primary="Desbloquear contato" />
													</ListItem>
												</UnblockContactDialog>
											)
												: <BlockContactDialog
													clientId={client?.id as number}
													inboxChannelId={inboxChannelId}
													hasTooltip
													onSuccess={blockContactOnSuccess}
												>
													<ListItem
														button>
														<BlockContactIcon style={{ width: 22, height: 22 }} />
														<Divider orientation="vertical" size={1} />
														<ListItemText primary="Bloquear contato" />
													</ListItem>
												</BlockContactDialog>
											}
										</ContactGroupPermissionGate>
									)
								}
							</List>
						</Popover>
					</Grid>

				</>
			)}
		</>
	), [
		currentChatAttendanceStatus,
		moreOptionsMenuIsOpen,
		isSyncChatMessages,
		chatGlobalStateStore.chat.current?.status,
		chatGlobalStateStore.chat.current?.title,
		chatGlobalStateStore.client.current?.picture_key,
		contactPhoneNumber,
		isBlockedContact,
		canManageAttendanceWithoutPermission,
		canTransferWithoutPermission
	])
}

export default ConversationHeader
